// EPSE PDF Report Generator
import jsPDF from 'jspdf';
import { 
  EPSESite, 
  EPSEProfile, 
  EPSESimulation, 
  DataQualityReport,
  SimulationKPIs,
  ProfileStatistics 
} from '@/types/epse';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

interface EPSEReportData {
  site: EPSESite;
  profiles: EPSEProfile[];
  simulation?: EPSESimulation;
  qualityReport?: DataQualityReport;
  quarterDataCount: number;
  dateRange?: { start: string; end: string };
}

export async function generateEPSEReport(data: EPSEReportData): Promise<Blob> {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let y = margin;
  
  const addPage = () => {
    doc.addPage();
    y = margin;
  };
  
  const checkPageBreak = (neededHeight: number) => {
    if (y + neededHeight > pageHeight - margin) {
      addPage();
    }
  };
  
  // Helper functions
  const addTitle = (text: string, size: number = 16) => {
    checkPageBreak(20);
    doc.setFontSize(size);
    doc.setFont('helvetica', 'bold');
    doc.text(text, margin, y);
    y += size * 0.5 + 5;
  };
  
  const addParagraph = (text: string) => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(text, pageWidth - 2 * margin);
    checkPageBreak(lines.length * 5 + 5);
    doc.text(lines, margin, y);
    y += lines.length * 5 + 5;
  };
  
  const addTable = (headers: string[], rows: string[][]) => {
    const colWidth = (pageWidth - 2 * margin) / headers.length;
    const rowHeight = 8;
    
    checkPageBreak((rows.length + 1) * rowHeight + 10);
    
    // Headers
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, y - 5, pageWidth - 2 * margin, rowHeight, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    headers.forEach((header, i) => {
      doc.text(header, margin + i * colWidth + 2, y);
    });
    y += rowHeight;
    
    // Rows
    doc.setFont('helvetica', 'normal');
    rows.forEach(row => {
      row.forEach((cell, i) => {
        doc.text(String(cell), margin + i * colWidth + 2, y);
      });
      y += rowHeight;
    });
    y += 5;
  };
  
  // === TITLE PAGE ===
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('Energieanalyse & Simulatie Rapport', margin, 50);
  
  doc.setFontSize(18);
  doc.setFont('helvetica', 'normal');
  doc.text(data.site.name, margin, 65);
  
  if (data.site.organization?.name) {
    doc.setFontSize(14);
    doc.text(data.site.organization.name, margin, 80);
  }
  
  doc.setFontSize(12);
  doc.text(`Gegenereerd op: ${format(new Date(), 'd MMMM yyyy', { locale: nl })}`, margin, 100);
  
  if (data.dateRange) {
    doc.text(`Dataperiode: ${data.dateRange.start} - ${data.dateRange.end}`, margin, 112);
  }
  
  // Site info box
  doc.setFillColor(245, 245, 245);
  doc.rect(margin, 130, pageWidth - 2 * margin, 40, 'F');
  
  doc.setFontSize(10);
  doc.text(`Contractlimiet: ${data.site.connection_kw_limit} kW`, margin + 5, 142);
  if (data.site.annual_kwh_total) {
    doc.text(`Jaarverbruik (opgegeven): ${Math.round(data.site.annual_kwh_total).toLocaleString('nl-NL')} kWh`, margin + 5, 154);
  }
  if (data.site.pv_installed_kwp) {
    doc.text(`PV Vermogen: ${data.site.pv_installed_kwp} kWp`, margin + 5, 166);
  }
  
  // === PAGE 2: DATA PROCESSING ===
  addPage();
  addTitle('1. Data Processing', 16);
  
  addParagraph(`Deze analyse is gebaseerd op ${data.quarterDataCount.toLocaleString('nl-NL')} kwartierwaarden (15-minutenintervallen). Elke waarde is voorzien van tijdcontextlabels voor een gedetailleerde analyse.`);
  
  addTitle('1.1 Tijdcontext Definitie', 12);
  addParagraph('Elke kwartierwaarde wordt automatisch voorzien van de volgende labels:');
  
  addTable(
    ['Label', 'Definitie', 'Waarden'],
    [
      ['Dagtype', 'Classificatie van de dag', 'Werkdag, Weekend, Feestdag (NL)'],
      ['Seizoen', 'Meteorologisch seizoen', 'Winter (dec-feb), Lente (mrt-mei), Zomer (jun-aug), Herfst (sep-nov)'],
      ['Nacht', 'Nachtelijke uren', '22:00 - 06:00'],
      ['Kwartier', 'Index van kwartier op dag', '0-95 (96 kwartieren per dag)'],
    ]
  );
  
  if (data.qualityReport) {
    addTitle('1.2 Datakwaliteit', 12);
    addParagraph(`Dekking score: ${data.qualityReport.coverage_score}%`);
    
    if (data.qualityReport.warnings.length > 0) {
      addParagraph('Waarschuwingen:');
      data.qualityReport.warnings.forEach(warning => {
        addParagraph(`• [${warning.severity.toUpperCase()}] ${warning.message}`);
      });
    } else {
      addParagraph('Geen datakwaliteitsproblemen gedetecteerd.');
    }
  }
  
  // === PAGE 3: METHODOLOGY ===
  addPage();
  addTitle('2. Methodologie', 16);
  
  addTitle('2.1 Profielberekening', 12);
  addParagraph('Energieprofielen worden berekend door alle kwartierwaarden te groeperen per tijdcontext (dagtype × seizoen) en het gemiddelde vermogen per kwartier van de dag te bepalen. Dit resulteert in een genormaliseerde curve van 96 waarden die het typische verbruikspatroon weergeeft.');
  
  addTitle('2.2 Statistieken', 12);
  addParagraph('De volgende statistieken worden berekend:');
  
  addTable(
    ['Statistiek', 'Beschrijving'],
    [
      ['P50 (Mediaan)', 'Waarde waarboven 50% van de metingen ligt'],
      ['P95', 'Waarde waarboven 5% van de metingen ligt'],
      ['P99', 'Waarde waarboven 1% van de metingen ligt'],
      ['Maximum', 'Hoogst gemeten vermogen'],
      ['Ramp Rate', 'Gemiddelde vermogensverandering per 15 minuten (ΔkW)'],
      ['Nachtbasis', 'P10 van nachtelijke uren (basislast)'],
    ]
  );
  
  addTitle('2.3 BESS Simulatie', 12);
  addParagraph('De Battery Energy Storage System (BESS) simulatie modelleert het gedrag van een batterijsysteem om pieken boven de contractlimiet te reduceren. Twee dispatch-strategieën zijn beschikbaar:');
  
  addParagraph('Rule-Based: Realistische strategie die de batterij laadt wanneer het netto-verbruik onder de limiet ligt en ontlaadt wanneer de limiet overschreden wordt. Dit is een causale strategie die geen toekomstige informatie gebruikt.');
  
  addParagraph('Perfect Foresight: Theoretische bovengrens die optimaliseert met volledige kennis van het dagprofiel. Dit geeft de maximaal haalbare piekreductie weer, maar is in de praktijk niet realiseerbaar.');
  
  // === PAGE 4: PROFILE ANALYSIS ===
  addPage();
  addTitle('3. Profielanalyse', 16);
  
  const overallProfile = data.profiles.find(p => !p.day_type && !p.season);
  if (overallProfile) {
    addTitle('3.1 Algemeen Profiel', 12);
    addParagraph('Statistieken over alle beschikbare data:');
    
    const stats = overallProfile.statistics;
    addTable(
      ['Statistiek', 'Waarde', 'Eenheid'],
      [
        ['Maximum', stats.max.toFixed(1), 'kW'],
        ['P99', stats.p99.toFixed(1), 'kW'],
        ['P95', stats.p95.toFixed(1), 'kW'],
        ['Gemiddeld', stats.mean.toFixed(1), 'kW'],
        ['Standaarddeviatie', stats.std_dev.toFixed(1), 'kW'],
        ['Ramp Rate (gem.)', stats.ramp_rate_avg.toFixed(1), 'ΔkW/15m'],
        ['Ramp Rate (max)', stats.ramp_rate_max.toFixed(1), 'ΔkW/15m'],
        ['Nachtbasis (P10)', stats.night_base_p10.toFixed(1), 'kW'],
        ['Totaal Verbruik', Math.round(stats.total_kwh).toLocaleString('nl-NL'), 'kWh'],
      ]
    );
  }
  
  // Seasonal profiles
  const seasonalProfiles = data.profiles.filter(p => p.season && p.day_type === 'werkdag');
  if (seasonalProfiles.length > 0) {
    addTitle('3.2 Seizoensvariatie (Werkdagen)', 12);
    addParagraph('Vergelijking van werkdagprofielen per seizoen:');
    
    addTable(
      ['Seizoen', 'Max kW', 'P95 kW', 'Gem. kW'],
      seasonalProfiles.map(p => [
        p.season?.charAt(0).toUpperCase() + (p.season?.slice(1) || ''),
        p.statistics.max.toFixed(1),
        p.statistics.p95.toFixed(1),
        p.statistics.mean.toFixed(1),
      ])
    );
  }
  
  // === PAGE 5: SIMULATION RESULTS ===
  if (data.simulation && data.simulation.status === 'completed') {
    addPage();
    addTitle('4. BESS Simulatieresultaten', 16);
    
    addTitle('4.1 Configuratie', 12);
    addTable(
      ['Parameter', 'Waarde'],
      [
        ['Capaciteit', `${data.simulation.bess_config.capacity_kwh} kWh`],
        ['Vermogen', `${data.simulation.bess_config.power_kw} kW`],
        ['C-rate', `${(data.simulation.bess_config.power_kw / data.simulation.bess_config.capacity_kwh).toFixed(2)}C`],
        ['Efficiëntie (laden)', `${(data.simulation.bess_config.efficiency_charge * 100).toFixed(0)}%`],
        ['Efficiëntie (ontladen)', `${(data.simulation.bess_config.efficiency_discharge * 100).toFixed(0)}%`],
        ['SOC Bereik', `${(data.simulation.bess_config.soc_min * 100).toFixed(0)}% - ${(data.simulation.bess_config.soc_max * 100).toFixed(0)}%`],
        ['Dispatch Mode', data.simulation.dispatch_mode === 'rule_based' ? 'Rule-Based' : 'Perfect Foresight'],
      ]
    );
    
    addTitle('4.2 Resultaten', 12);
    const kpis = data.simulation.kpis;
    addTable(
      ['KPI', 'Waarde', 'Beschrijving'],
      [
        ['Piekreductie', `${kpis.peak_reduction_kw.toFixed(1)} kW (${kpis.peak_reduction_percent.toFixed(1)}%)`, 'Reductie van maximale netpiek'],
        ['Overschrijdingen', `${kpis.peak_overruns_count} (${kpis.peak_overruns_duration_hours.toFixed(1)} uur)`, 'Resterende momenten boven limiet'],
        ['BESS Cycli', kpis.bess_cycles.toFixed(1), 'Equivalente volledige laadcycli'],
        ['Throughput', `${(kpis.bess_throughput_kwh / 1000).toFixed(2)} MWh`, 'Totale energie door batterij'],
        ['Netimport', `${Math.round(kpis.import_kwh).toLocaleString('nl-NL')} kWh`, 'Totale import van net'],
      ]
    );
  }
  
  // === PAGE 6: INTERPRETATION ===
  addPage();
  addTitle('5. Interpretatie', 16);
  
  addParagraph('De gepresenteerde analyse biedt inzicht in het energieprofiel van de locatie en de potentiële voordelen van batterijopslag voor piekreductie.');
  
  addTitle('5.1 Belangrijke Bevindingen', 12);
  
  if (overallProfile) {
    const stats = overallProfile.statistics;
    const exceedanceKw = stats.max - data.site.connection_kw_limit;
    
    if (exceedanceKw > 0) {
      addParagraph(`Het maximale gemeten vermogen (${stats.max.toFixed(0)} kW) overschrijdt de contractlimiet (${data.site.connection_kw_limit} kW) met ${exceedanceKw.toFixed(0)} kW. Dit kan leiden tot transportkosten of de noodzaak van capaciteitsuitbreiding.`);
    } else {
      addParagraph(`Het maximale gemeten vermogen (${stats.max.toFixed(0)} kW) blijft binnen de contractlimiet (${data.site.connection_kw_limit} kW). Er is ${Math.abs(exceedanceKw).toFixed(0)} kW headroom beschikbaar.`);
    }
    
    if (stats.ramp_rate_max > stats.mean * 0.5) {
      addParagraph(`De maximale ramp rate (${stats.ramp_rate_max.toFixed(0)} kW/15m) is significant, wat wijst op snel wisselende belasting. Dit kan relevant zijn voor netbalancering en batterij-sizing.`);
    }
  }
  
  if (data.simulation && data.simulation.status === 'completed') {
    addTitle('5.2 BESS Potentieel', 12);
    const kpis = data.simulation.kpis;
    
    if (kpis.peak_reduction_percent > 20) {
      addParagraph(`De BESS-simulatie toont een significante piekreductie van ${kpis.peak_reduction_percent.toFixed(1)}%. Dit kan substantiële besparingen op transportkosten opleveren.`);
    } else if (kpis.peak_reduction_percent > 0) {
      addParagraph(`De BESS-simulatie toont een piekreductie van ${kpis.peak_reduction_percent.toFixed(1)}%. De toegevoegde waarde hangt af van de specifieke tarievenstructuur.`);
    }
    
    if (kpis.peak_overruns_count > 0) {
      addParagraph(`Er resteren ${kpis.peak_overruns_count} momenten van overschrijding (${kpis.peak_overruns_duration_hours.toFixed(1)} uur totaal). Overweeg een grotere batterijcapaciteit of -vermogen om deze volledig te elimineren.`);
    } else {
      addParagraph('Alle piekbelastingen worden effectief door de batterij opgevangen. Er zijn geen resterende overschrijdingen van de contractlimiet.');
    }
  }
  
  // === PAGE 7: APPENDIX ===
  addPage();
  addTitle('6. Bijlagen', 16);
  
  addTitle('6.1 Rekenformules', 12);
  addParagraph('Percentiel: Pn = waarde op positie (n/100) × N in gesorteerde dataset');
  addParagraph('Ramp Rate: ΔkW = |kW(t) - kW(t-1)|');
  addParagraph('Energie: kWh = Σ(kW × 0.25) voor kwartierwaarden');
  addParagraph('BESS Cycli: (laad_kWh + ontlaad_kWh) / (2 × capaciteit_kWh)');
  
  addTitle('6.2 Aannames', 12);
  addParagraph('• Kwartierwaarden representeren het gemiddelde vermogen over het 15-minuten interval');
  addParagraph('• Batterij-efficiëntie is constant over het gehele SOC-bereik');
  addParagraph('• Geen degradatie van batterijcapaciteit binnen de simulatieperiode');
  addParagraph('• Rule-based dispatch reageert instantaan op vermogensveranderingen');
  
  addTitle('6.3 Disclaimer', 12);
  addParagraph('Dit rapport is gegenereerd op basis van de aangeleverde kwartierdata. De nauwkeurigheid van de analyse is afhankelijk van de kwaliteit en volledigheid van de brondata. De BESS-simulatie is een theoretisch model en werkelijke resultaten kunnen afwijken door factoren zoals batterijdegradatie, omgevingstemperatuur en werkelijke besturingsalgoritmen.');
  
  // Footer on last page
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text(
    `Gegenereerd met EPSE (Energy Profile & Simulation Engine) • ${format(new Date(), 'd MMMM yyyy HH:mm', { locale: nl })}`,
    pageWidth / 2,
    pageHeight - 10,
    { align: 'center' }
  );
  
  return doc.output('blob');
}

export function getReportFilename(siteName: string): string {
  const date = format(new Date(), 'yyyyMMdd');
  const safeName = siteName.replace(/[^a-zA-Z0-9]/g, '_');
  return `EPSE_Report_${safeName}_${date}.pdf`;
}
